import { type JSX, useState } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useForwardSlottedEvents } from "@frontend/bridge/useForwardSlottedEvents";

import { ScrollArea } from "./ScrollArea";
import {
  type ScrollAreaDataProps,
  scrollAreaDataSchema,
} from "./ScrollArea.schema";
import { ScrollAreaContent } from "./ScrollAreaContent";
import { ScrollAreaScrollbar } from "./ScrollAreaScrollbar";
import { ScrollAreaThumb } from "./ScrollAreaThumb";
import { ScrollAreaViewport } from "./ScrollAreaViewport";
import { SCROLL_AREA_TAG_NAME } from "./constants";

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [SCROLL_AREA_TAG_NAME]: { props: ScrollAreaDataProps; handle: undefined };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-scroll-area": HTMLElement;
  }
}

function RWCScrollArea({
  fade,
  expandOnHover,
  alwaysVisible,
}: ScrollAreaDataProps): JSX.Element {
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);

  useForwardSlottedEvents(viewport);

  return (
    <ScrollArea style={{ height: "100%" }}>
      <ScrollAreaViewport ref={setViewport} fade={fade}>
        <ScrollAreaContent style={{ minWidth: 0 }}>
          <slot />
        </ScrollAreaContent>
      </ScrollAreaViewport>
      <ScrollAreaScrollbar
        expandOnHover={expandOnHover}
        alwaysVisible={alwaysVisible}
      >
        <ScrollAreaThumb />
      </ScrollAreaScrollbar>
    </ScrollArea>
  );
}

componentManager.register(
  rwc(SCROLL_AREA_TAG_NAME, {
    component: RWCScrollArea,
    dataSchema: scrollAreaDataSchema,
    usesSlots: true,
  }),
);
